import type { CmmdProjectRepository } from "@cmmd-forge/contracts";
import { cmmdApiClient, type CmmdApiClientInterface } from "~/environments/cmmdApi";

/** A Forge execution project to route into when a CMMD project is linked. */
export interface ForgeExecutionTarget {
  readonly environmentId: string;
  readonly projectId: string;
}

/** A specific Forge execution thread to route into after CMMD starts work. */
export interface ForgeThreadTarget {
  readonly environmentId: string;
  readonly threadId: string;
}

/**
 * Single source of truth for "is this CMMD project linked to a Forge execution
 * project?". Pure mapper from the project's repository/link payload to the Forge
 * route target, or `null` when the project has no linked execution project (in
 * which case it stays under Projects).
 *
 * The link record uses string ids (`forgeEnvironmentId` / `forgeProjectId`).
 * They no longer address a page of their own — the execution workspace route
 * was removed — but they still scope a project's threads, work sessions, and
 * execution status.
 */
export function toForgeExecutionTarget(
  repository: CmmdProjectRepository | null | undefined,
): ForgeExecutionTarget | null {
  const link = repository?.forgeLink;
  if (!link) return null;
  const environmentId = link.forgeEnvironmentId?.trim();
  const projectId = link.forgeProjectId?.trim();
  if (!environmentId || !projectId) return null;
  return { environmentId, projectId };
}

/**
 * Resolve, for a CMMD project, whether it routes into a Forge execution project.
 * Resolved lazily (on demand) rather than eagerly for every project to avoid an
 * N+1 of `/repository` calls across the project list.
 */
export async function resolveForgeExecutionTarget(
  projectId: string,
  client: Pick<CmmdApiClientInterface, "getProjectRepository"> = cmmdApiClient,
): Promise<ForgeExecutionTarget | null> {
  const repository = await client.getProjectRepository(projectId);
  return toForgeExecutionTarget(repository);
}
